import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import { companiesAPI, documentsAPI } from '../services/api';
import {
  COMPANY_DOCUMENT_SECTIONS,
  bucketCompanyDocumentsBySection,
} from './Company/companyDocumentSections';
import { formatFileSize, formatDocumentType } from '../utils/documentUtils';

const buildUploadForm = (documentType = 'OTHER') => ({
  documentType,
  description: '',
  documentDate: '',
  expiryDate: '',
  reportingMonth: '',
  fiscalYear: '',
});

const UploaderCompanyUploadDesk: React.FC = () => {
  const { id } = useParams();
  const [company, setCompany] = useState<any | null>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadForm, setUploadForm] = useState(buildUploadForm());

  const [editOpen, setEditOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<any | null>(null);

  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState<any | null>(null);
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replaceReason, setReplaceReason] = useState('');

  const grouped = useMemo(() => bucketCompanyDocumentsBySection(documents), [documents]);

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [companyRes, docsRes] = await Promise.all([
        companiesAPI.getById(String(id)),
        documentsAPI.getByCompany(String(id)),
      ]);
      setCompany(companyRes.data);
      setDocuments(docsRes.data?.data || docsRes.data || []);
    } catch (e: any) {
      if (e?.response?.status === 403) {
        setError('You do not have access to this company.');
      } else {
        setError('Failed to load company upload desk.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openUpload = (documentType: string) => {
    setUploadFiles([]);
    setUploadForm(buildUploadForm(documentType));
    setUploadOpen(true);
  };

  const handleUpload = async () => {
    if (!id || uploadFiles.length === 0) return;
    try {
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);
        formData.append('companyId', String(id));
        formData.append('documentType', uploadForm.documentType);
        if (uploadForm.description) formData.append('description', uploadForm.description);
        if (uploadForm.documentDate) formData.append('documentDate', uploadForm.documentDate);
        if (uploadForm.expiryDate) formData.append('expiryDate', uploadForm.expiryDate);
        if (uploadForm.reportingMonth) formData.append('reportingMonth', uploadForm.reportingMonth);
        if (uploadForm.fiscalYear) formData.append('fiscalYear', uploadForm.fiscalYear);
        await documentsAPI.upload(formData);
      }
      toast.success('Documents uploaded successfully');
      setUploadOpen(false);
      await loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Upload failed');
    }
  };

  const handleDownload = async (docId: string, filename: string) => {
    try {
      const response = await documentsAPI.download(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const openEdit = (doc: any) => {
    setEditingDocument({
      id: doc.id,
      title: doc.title || '',
      description: doc.description || '',
      documentType: doc.documentType || 'OTHER',
      documentDate: doc.documentDate ? new Date(doc.documentDate).toISOString().slice(0, 10) : '',
      expiryDate: doc.expiryDate ? new Date(doc.expiryDate).toISOString().slice(0, 10) : '',
      reportingMonth: doc.reportingMonth || '',
      fiscalYear: doc.fiscalYear ?? '',
      notes: doc.notes || '',
    });
    setEditOpen(true);
  };

  const handleSaveMetadata = async () => {
    if (!editingDocument) return;
    try {
      await documentsAPI.update(editingDocument.id, {
        title: editingDocument.title,
        description: editingDocument.description,
        documentType: editingDocument.documentType,
        documentDate: editingDocument.documentDate || undefined,
        expiryDate: editingDocument.expiryDate || undefined,
        reportingMonth: editingDocument.reportingMonth || undefined,
        fiscalYear:
          editingDocument.fiscalYear === '' ? undefined : Number(editingDocument.fiscalYear),
        notes: editingDocument.notes || undefined,
      });
      toast.success('Document metadata updated');
      setEditOpen(false);
      setEditingDocument(null);
      await loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to update metadata');
    }
  };

  const openReplace = (doc: any) => {
    setReplaceTarget(doc);
    setReplaceFile(null);
    setReplaceReason('');
    setReplaceOpen(true);
  };

  const handleReplace = async () => {
    if (!replaceTarget?.id || !replaceFile) {
      toast.error('Select a replacement file');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', replaceFile);
      if (replaceReason) formData.append('correctionReason', replaceReason);
      await documentsAPI.replace(replaceTarget.id, formData);
      toast.success('Replacement version uploaded');
      setReplaceOpen(false);
      setReplaceTarget(null);
      setReplaceFile(null);
      await loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to replace file');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading upload desk…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/my-companies" underline="hover" color="inherit">
          My Companies
        </Link>
        <Typography color="text.primary">{company?.name || 'Upload Desk'}</Typography>
      </Breadcrumbs>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h5" fontWeight="bold">{company?.name}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            {company?.jurisdiction && <Chip label={company.jurisdiction} />}
            {company?.registrationNumber && (
              <Chip variant="outlined" label={company.registrationNumber} />
            )}
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {COMPANY_DOCUMENT_SECTIONS.map((section) => {
          const sectionDocs = grouped[section.key];
          return (
            <Grid item xs={12} md={4} key={section.key}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">{section.title}</Typography>
                      <Chip size="small" label={sectionDocs.length} />
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<UploadFileIcon />}
                      onClick={() => openUpload(section.defaultDocumentType)}
                    >
                      {section.uploadButtonLabel}
                    </Button>
                    <Divider />
                    {sectionDocs.length === 0 ? (
                      <Alert severity="info">{section.emptyState}</Alert>
                    ) : (
                      <List dense disablePadding>
                        {sectionDocs.map((doc: any) => (
                          <ListItem
                            key={doc.id}
                            divider
                            secondaryAction={
                              <Stack direction="row" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  aria-label="edit metadata"
                                  onClick={() => openEdit(doc)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  aria-label="replace file"
                                  disabled={doc.status === 'SUPERSEDED'}
                                  onClick={() => openReplace(doc)}
                                >
                                  <AutorenewIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  aria-label="download file"
                                  onClick={() => handleDownload(doc.id, doc.filename)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            }
                          >
                            <ListItemText
                              primary={
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Typography variant="body2" fontWeight={600}>
                                    {doc.title || doc.filename}
                                  </Typography>
                                  {doc.status === 'SUPERSEDED' && (
                                    <Chip size="small" color="warning" label="Superseded" />
                                  )}
                                </Stack>
                              }
                              secondary={
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {doc.filename} • {formatFileSize(doc.fileSize || 0)} •{' '}
                                    {formatDocumentType(doc.documentType)}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {doc.reportingMonth && (
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`Month: ${doc.reportingMonth}`}
                                      />
                                    )}
                                    {doc.fiscalYear && (
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`FY: ${doc.fiscalYear}`}
                                      />
                                    )}
                                    {doc.documentVersion && (
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`v${doc.documentVersion}`}
                                      />
                                    )}
                                  </Stack>
                                </Stack>
                              }
                              secondaryTypographyProps={{ component: 'div' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload Documents</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
              Select Files
              <input
                hidden
                type="file"
                multiple
                onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
              />
            </Button>
            {uploadFiles.length > 0 && (
              <List dense>
                {uploadFiles.map((file, index) => (
                  <ListItem
                    key={`${file.name}-${index}`}
                    secondaryAction={
                      <IconButton
                        size="small"
                        onClick={() => setUploadFiles((prev) => prev.filter((_, idx) => idx !== index))}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024).toFixed(1)} KB`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            <Select
              value={uploadForm.documentType}
              onChange={(e) => setUploadForm((prev) => ({ ...prev, documentType: e.target.value }))}
            >
              <MenuItem value="FINANCIAL_STATEMENT">Financial Statement</MenuItem>
              <MenuItem value="INCORPORATION">Incorporation</MenuItem>
              <MenuItem value="SHAREHOLDER_REGISTER">Shareholder Register</MenuItem>
              <MenuItem value="BOARD_RESOLUTION">Board Resolution</MenuItem>
              <MenuItem value="FILING">Filing</MenuItem>
              <MenuItem value="CERTIFICATE">Certificate</MenuItem>
              <MenuItem value="CORRESPONDENCE">Correspondence</MenuItem>
              <MenuItem value="CONTRACT">Contract</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
            <TextField
              label="Description (optional)"
              value={uploadForm.description}
              onChange={(e) => setUploadForm((prev) => ({ ...prev, description: e.target.value }))}
            />
            <TextField
              label="Document Date (optional)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={uploadForm.documentDate}
              onChange={(e) => setUploadForm((prev) => ({ ...prev, documentDate: e.target.value }))}
            />
            <TextField
              label="Expiry Date (optional)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={uploadForm.expiryDate}
              onChange={(e) => setUploadForm((prev) => ({ ...prev, expiryDate: e.target.value }))}
            />
            <TextField
              label="Calendar Month (optional)"
              type="month"
              InputLabelProps={{ shrink: true }}
              value={uploadForm.reportingMonth}
              onChange={(e) =>
                setUploadForm((prev) => ({ ...prev, reportingMonth: e.target.value }))
              }
            />
            <TextField
              label="Fiscal Year (optional)"
              type="number"
              value={uploadForm.fiscalYear}
              onChange={(e) => setUploadForm((prev) => ({ ...prev, fiscalYear: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploadFiles.length === 0}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Metadata</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={editingDocument?.title || ''}
              onChange={(e) =>
                setEditingDocument((prev: any) => ({ ...prev, title: e.target.value }))
              }
            />
            <TextField
              label="Description"
              value={editingDocument?.description || ''}
              onChange={(e) =>
                setEditingDocument((prev: any) => ({ ...prev, description: e.target.value }))
              }
              multiline
              rows={2}
            />
            <Select
              value={editingDocument?.documentType || 'OTHER'}
              onChange={(e) =>
                setEditingDocument((prev: any) => ({ ...prev, documentType: e.target.value }))
              }
            >
              <MenuItem value="FINANCIAL_STATEMENT">Financial Statement</MenuItem>
              <MenuItem value="INCORPORATION">Incorporation</MenuItem>
              <MenuItem value="SHAREHOLDER_REGISTER">Shareholder Register</MenuItem>
              <MenuItem value="BOARD_RESOLUTION">Board Resolution</MenuItem>
              <MenuItem value="FILING">Filing</MenuItem>
              <MenuItem value="CERTIFICATE">Certificate</MenuItem>
              <MenuItem value="CORRESPONDENCE">Correspondence</MenuItem>
              <MenuItem value="CONTRACT">Contract</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
            <TextField
              label="Calendar Month (optional)"
              type="month"
              InputLabelProps={{ shrink: true }}
              value={editingDocument?.reportingMonth || ''}
              onChange={(e) =>
                setEditingDocument((prev: any) => ({ ...prev, reportingMonth: e.target.value }))
              }
            />
            <TextField
              label="Fiscal Year (optional)"
              type="number"
              value={editingDocument?.fiscalYear ?? ''}
              onChange={(e) =>
                setEditingDocument((prev: any) => ({ ...prev, fiscalYear: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMetadata}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={replaceOpen} onClose={() => setReplaceOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Replace File with New Version</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              The current file will be marked as superseded and retained in history.
            </Typography>
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
              Select Replacement File
              <input
                hidden
                type="file"
                onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
              />
            </Button>
            {replaceFile && (
              <Alert severity="info">
                Selected: {replaceFile.name} ({(replaceFile.size / 1024).toFixed(1)} KB)
              </Alert>
            )}
            <TextField
              label="Correction reason (optional)"
              value={replaceReason}
              onChange={(e) => setReplaceReason(e.target.value)}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleReplace} disabled={!replaceFile}>
            Replace as New Version
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploaderCompanyUploadDesk;
